import { AxiosError } from 'axios';

/**
 * API Error response structure from backend
 */
interface ApiErrorResponse {
  message?: string;
  error?: string;
  details?: Record<string, string[]>;
}

/**
 * Extracts a user-friendly error message from various error types
 * @param error - The error object (can be AxiosError, Error, or unknown)
 * @param fallbackMessage - Default message if error cannot be parsed
 * @returns User-friendly error message string
 */
export function getErrorMessage(error: unknown, fallbackMessage = 'An error occurred'): string {
  // Handle Axios errors
  if (isAxiosError(error)) {
    const data = error.response?.data as ApiErrorResponse | undefined;
    return data?.message || data?.error || error.message || fallbackMessage;
  }

  // Handle standard Error objects
  if (error instanceof Error) {
    return error.message || fallbackMessage;
  }

  // Handle string errors
  if (typeof error === 'string') {
    return error;
  }

  // Handle objects with message property
  if (error && typeof error === 'object' && 'message' in error) {
    return String((error as { message: unknown }).message) || fallbackMessage;
  }

  return fallbackMessage;
}

/**
 * Type guard to check if an error is an AxiosError
 */
export function isAxiosError(error: unknown): error is AxiosError<ApiErrorResponse> {
  return (
    error !== null &&
    typeof error === 'object' &&
    'isAxiosError' in error &&
    (error as AxiosError).isAxiosError === true
  );
}

/**
 * Extracts validation errors from API response
 * @param error - The error object
 * @returns Record of field names to error messages, or null if not a validation error
 */
export function getValidationErrors(error: unknown): Record<string, string[]> | null {
  if (isAxiosError(error)) {
    const data = error.response?.data as ApiErrorResponse | undefined;
    return data?.details || null;
  }
  return null;
}
